import {
  type CSSProperties,
  type DependencyList,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  SciChartSurface,
  SciChartJSLightTheme,
  NumberRange,
  type TSciChart,
} from 'scichart';
import classNames from 'classnames';
import { Button } from '@carbon/react';
import { Reset } from '@carbon/react/icons';
import {
  SciChartExportImageButton,
  createSciChartElementHooks,
  createSciChartExporter,
  type ChartImageExporter,
} from 'components/chart-export';
import { ensureWasmConfigured } from './ensure-wasm-configured';
import styles from './scichart-view.module.scss';

// An axis counts as "zoomed" once its visible range shifts by more than 0.1%
// of its home span — catches real zoom/pan while ignoring float drift.
const ZOOM_CHANGE_TOLERANCE = 0.001;

// Container-vs-canvas size difference (px) below which a resize is ignored.
const SIZE_EPSILON_PX = 1;

/** Surface + WASM context handed to the consumer's init/render callbacks. */
export interface SciChartInitContext {
  surface: SciChartSurface;
  wasmContext: TSciChart;
}

export interface SciChartViewProps {
  /**
   * Runs ONCE after the surface is created — set up axes/modifiers, not
   * prop-driven series (those go in `renderData`).
   */
  initChart: (ctx: SciChartInitContext) => void;
  /**
   * Builds/rebuilds data-driven content (series, annotations) after init and
   * whenever `renderDeps` change. The consumer clears what it previously added.
   */
  renderData: (ctx: SciChartInitContext) => void;
  /** Dependency list that re-triggers `renderData` (like a useEffect dep list). */
  renderDeps: DependencyList;
  /** Optional hook to tweak the light theme before the surface is created. */
  themeOverrides?: (theme: SciChartJSLightTheme) => void;
  /** Pixel height (number) or any CSS height for the chart canvas. */
  height?: number | string;
  /** Extra class on the root wrapper. */
  className?: string;
  /** Shows a built-in loading overlay (SciChart has no native showLoading). */
  isLoading?: boolean;
  /**
   * Absolutely-positioned overlay content (tooltips, legends, custom loading).
   * Rendered inside the relative root, above the canvas.
   */
  children?: ReactNode;
  /** Notified once the surface is created (and again as null on teardown). */
  onSurfaceReady?: (surface: SciChartSurface | null) => void;
  /**
   * Notified with a ready-to-use image exporter for this view once the surface is
   * created (and `null` on teardown) — for callers that capture the chart to a Blob
   * without the built-in button (e.g. bulk content export). Opt-in: when omitted,
   * no exporter is exposed. The exporter closes over this view's surface + canvas
   * host + element hooks, so it captures the exact rendered chart.
   */
  onExporterReady?: (exporter: ChartImageExporter | null) => void;
  /**
   * A ref to an extra DOM element (e.g. this chart's legend) to include in image exports. It lives
   * OUTSIDE the captured canvas (a sibling of it — custom-chart / spec-chart), so the exporter
   * stacks it above the exported image. Domain-neutral: the caller maps its own element (a legend)
   * to this generic slot. Omit when there's nothing extra to export.
   */
  getExportOverlay?: RefObject<HTMLElement | null>;
  /**
   * Unique id for the host div. SciChart derives its canvas id from this and
   * deletes earlier surfaces sharing an id; a random fallback is used if omitted.
   */
  id?: string;
  /**
   * Shows a self-contained "Reset Zoom" button that restores every axis to its
   * range after the last `renderData` (the chart's home view).
   */
  showResetZoom?: boolean;
  /**
   * When provided, renders an icon-only image-export trigger (top-right) wired
   * to this view's surface. Captures the inner canvas host, so the trigger
   * itself never appears in the exported image. Omit to render no trigger.
   */
  exportConfig?: { label: string; filenamePrefix: string };
}

// Generic, reusable SciChart host: owns the imperative lifecycle (WASM config,
// async create, delete-on-unmount, sizing) so consumers only supply the callbacks.
export const SciChartView = ({
  initChart,
  renderData,
  renderDeps,
  themeOverrides,
  height = 390,
  className,
  isLoading = false,
  children,
  onSurfaceReady,
  onExporterReady,
  getExportOverlay,
  id,
  showResetZoom = false,
  exportConfig,
}: SciChartViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const fallbackId = useRef(
    `scichart-${Math.random().toString(36).slice(2, 11)}`
  ).current;
  const divId = id ?? fallbackId;

  const surfaceRef = useRef<SciChartSurface | null>(null);
  // Scale axis-label/title fonts and series/marker weight to the export resolution.
  const exportElementHooks = useMemo(
    () => createSciChartElementHooks(() => surfaceRef.current),
    []
  );
  // A blob/download exporter bound to THIS view's surface + canvas host, exposed via
  // `onExporterReady`. Built ONLY when a consumer opts in — the vast majority of SciChart
  // views pass no callback, so they do zero exporter work (nothing added elsewhere).
  const exporter = useMemo<ChartImageExporter | null>(() => {
    if (!onExporterReady) {
      return null;
    }
    return createSciChartExporter({
      getSurface: () => surfaceRef.current,
      getContainer: () => canvasRef.current,
      elementHooks: exportElementHooks,
      // The overlay (e.g. the legend) is a sibling of the canvas (outside the captured container),
      // so pass it through to be composited above the exported image. A `RefObject` is stable, so
      // the mount-built exporter always reads the current node without rebuilding.
      getOverlayElement: () => getExportOverlay?.current ?? null,
      // Headless/bulk capture (content export) has no freeze overlay over the
      // shared WebGL canvas, so suppress its full-size repaint flash. The
      // built-in export button (below) builds its own exporter without this.
      captureOptions: { hideSharedCanvasOnResize: true },
    });
    // `getExportOverlay` is a stable RefObject, so it never actually retriggers this memo.
  }, [onExporterReady, exportElementHooks, getExportOverlay]);
  // Read via a ref inside the mount-only lifecycle effect (like the callback refs below), so
  // firing `onExporterReady` always uses the current exporter — never a stale capture if a
  // caller passes an unstable callback (the memo rebuilds but the mount effect won't re-run).
  const exporterRef = useRef(exporter);
  exporterRef.current = exporter;
  const wasmRef = useRef<TSciChart | null>(null);
  // Per-axis "home" ranges captured after each render (what Reset Zoom
  // restores), keyed by axis id so multi-axis charts each snap back correctly.
  const homeRangesRef = useRef<Map<string, { min: number; max: number }>>(
    new Map()
  );
  const [ready, setReady] = useState(false);
  // True once any axis range diverges from its home view — gates the Reset Zoom
  // button so it only appears after the user has actually zoomed/panned.
  const [isZoomed, setIsZoomed] = useState(false);

  // Latest callbacks via refs so the mount-only init effect and the deps effect
  // always invoke the freshest closures without re-subscribing.
  const initChartRef = useRef(initChart);
  initChartRef.current = initChart;
  const renderDataRef = useRef(renderData);
  renderDataRef.current = renderData;
  const themeOverridesRef = useRef(themeOverrides);
  themeOverridesRef.current = themeOverrides;
  const onSurfaceReadyRef = useRef(onSurfaceReady);
  onSurfaceReadyRef.current = onSurfaceReady;
  const onExporterReadyRef = useRef(onExporterReady);
  onExporterReadyRef.current = onExporterReady;

  // Snapshot every axis's current range as the home view (called after render).
  // Re-capturing home means "current === home", so the chart is not zoomed.
  const captureHomeRanges = (surface: SciChartSurface) => {
    const map = new Map<string, { min: number; max: number }>();
    [...surface.xAxes.asArray(), ...surface.yAxes.asArray()].forEach((axis) => {
      const range = axis.visibleRange;
      if (range) map.set(axis.id, { min: range.min, max: range.max });
    });
    homeRangesRef.current = map;
    setIsZoomed(false);
  };

  // True when any axis range has diverged from its captured home (zoom/pan),
  // using ZOOM_CHANGE_TOLERANCE so float noise doesn't trip it on a settled chart.
  const computeIsZoomed = (surface: SciChartSurface): boolean =>
    [...surface.xAxes.asArray(), ...surface.yAxes.asArray()].some((axis) => {
      const home = homeRangesRef.current.get(axis.id);
      const range = axis.visibleRange;
      if (!home || !range) return false;
      const tolerance = Math.abs(home.max - home.min) * ZOOM_CHANGE_TOLERANCE;
      return (
        Math.abs(range.min - home.min) > tolerance ||
        Math.abs(range.max - home.max) > tolerance
      );
    });

  // Restore every axis to its captured home range.
  const resetZoom = () => {
    const surface = surfaceRef.current;
    if (!surface) return;
    [...surface.xAxes.asArray(), ...surface.yAxes.asArray()].forEach((axis) => {
      const home = homeRangesRef.current.get(axis.id);
      if (home) axis.visibleRange = new NumberRange(home.min, home.max);
    });
    setIsZoomed(false);
  };

  // Surface lifecycle — create once on mount, delete on unmount.
  useEffect(() => {
    ensureWasmConfigured();
    let deleted = false;
    let resizeObserver: ResizeObserver | null = null;
    let resizeFrame: number | null = null;

    if (canvasRef.current && !surfaceRef.current) {
      const theme = new SciChartJSLightTheme();
      theme.sciChartBackground = '#FFFFFF';
      theme.majorGridLineBrush = '#e0e0e0';
      theme.minorGridLineBrush = '#eeeeee';
      theme.axisBandsFill = 'transparent';
      themeOverridesRef.current?.(theme);

      // create() streams WASM and is async; the surface is built in a promise
      // chain and the `deleted` flag handles unmount mid-create.
      SciChartSurface.create(canvasRef.current, { theme })
        .then(({ sciChartSurface, wasmContext }) => {
          // Lost the race with unmount — discard the freshly-created surface.
          if (deleted) {
            sciChartSurface.delete();
            return;
          }

          surfaceRef.current = sciChartSurface;
          wasmRef.current = wasmContext;
          initChartRef.current({ surface: sciChartSurface, wasmContext });
          onSurfaceReadyRef.current?.(sciChartSurface);
          onExporterReadyRef.current?.(exporterRef.current);

          // Track zoom/pan so Reset Zoom shows once an axis diverges from home;
          // subscriptions are torn down with the surface on delete().
          [
            ...sciChartSurface.xAxes.asArray(),
            ...sciChartSurface.yAxes.asArray(),
          ].forEach((axis) => {
            axis.visibleRangeChanged.subscribe(() => {
              if (deleted) return;
              setIsZoomed(computeIsZoomed(sciChartSurface));
            });
          });

          // Size the surface to its container before the first data render so
          // annotations/markers lay out against the correct viewport.
          // Last viewport size pushed to the surface. Every sync (mount, next
          // frame, observer) goes through this so an unchanged size is a no-op —
          // that is what stops a resize loop, and what keeps the observer's
          // initial notification from re-applying the mount sizing.
          let lastWidth = 0;
          let lastHeight = 0;
          const syncSize = () => {
            if (deleted) return;
            const container = containerRef.current;
            if (!container) return;
            const width = Math.max(1, Math.floor(container.clientWidth));
            const height = Math.max(1, Math.floor(container.clientHeight));
            if (width === lastWidth && height === lastHeight) return;
            // Growth-loop guard: compare against the canvas's ACTUAL current
            // size, not only our last push. If the container is within
            // SIZE_EPSILON_PX of what the canvas already is, applying would be
            // a no-op at best — and for a `height="100%"` consumer whose
            // container is a hair taller than the canvas (padding/border/inline
            // gap), repeatedly pushing near-equal sizes could grow the
            // container -> re-fire the observer -> grow again. Skipping keeps
            // that from being monotonic. Falls back to the last-pushed dedupe
            // above when the canvas size is not readable (unmeasured/0).
            // NOTE: `height="100%"` consumers (pre-drill 2D profile / gun-barrel,
            // spec-chart, custom-chart) still warrant a real-browser loop check
            // — layout feedback cannot be verified headless.
            const canvas = sciChartSurface.domCanvas2D;
            const actualWidth = canvas?.clientWidth ?? 0;
            const actualHeight = canvas?.clientHeight ?? 0;
            if (
              actualWidth > 0 &&
              actualHeight > 0 &&
              Math.abs(width - actualWidth) <= SIZE_EPSILON_PX &&
              Math.abs(height - actualHeight) <= SIZE_EPSILON_PX
            ) {
              return;
            }
            lastWidth = width;
            lastHeight = height;
            sciChartSurface.changeViewportSize(width, height);
          };
          syncSize();
          renderDataRef.current({ surface: sciChartSurface, wasmContext });
          // Re-sync next frame to catch container layout that settles after
          // mount, then snapshot the settled ranges as the Reset Zoom home view.
          requestAnimationFrame(() => {
            if (deleted) return;
            syncSize();
            captureHomeRanges(sciChartSurface);
            setReady(true);
          });

          // Keep the surface in step with its container (tearsheet/column layout
          // changes). Coalesced to one sync per frame; a collapsed container
          // (display:none, 0 size) is ignored so the last good size is kept.
          const container = containerRef.current;
          if (container && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
              if (resizeFrame !== null) return;
              resizeFrame = requestAnimationFrame(() => {
                resizeFrame = null;
                if (deleted) return;
                const current = containerRef.current;
                if (
                  !current ||
                  current.clientWidth <= 0 ||
                  current.clientHeight <= 0
                )
                  return;
                syncSize();
              });
            });
            resizeObserver.observe(container);
          }
        })
        .catch((err) => {
          console.error('SciChartView: failed to create surface', err);
        });
    }

    return () => {
      deleted = true;
      resizeObserver?.disconnect();
      resizeObserver = null;
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
      resizeFrame = null;
      onSurfaceReadyRef.current?.(null);
      onExporterReadyRef.current?.(null);
      surfaceRef.current?.delete();
      surfaceRef.current = null;
      wasmRef.current = null;
    };
    // Mount-only; callbacks read from refs above.
  }, []);

  // Re-render data when the consumer's deps change; skips until the surface
  // exists (the init effect does the first paint).
  useEffect(() => {
    const surface = surfaceRef.current;
    const wasmContext = wasmRef.current;
    if (!surface || !wasmContext) return;
    renderDataRef.current({ surface, wasmContext });
    // Re-snapshot the home view once the new data's ranges settle.
    requestAnimationFrame(() => {
      if (surfaceRef.current === surface) captureHomeRanges(surface);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, renderDeps);

  const rootStyle: CSSProperties = {
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div
      ref={containerRef}
      className={classNames(styles.root, className)}
      style={rootStyle}
    >
      <div ref={canvasRef} id={divId} className={styles.canvas} />
      {exportConfig && (
        <SciChartExportImageButton
          iconOnly
          placement="overlay"
          surfaceRef={surfaceRef}
          containerRef={canvasRef}
          chartLabel={exportConfig.label}
          filenamePrefix={exportConfig.filenamePrefix}
          elementHooks={exportElementHooks}
        />
      )}
      {showResetZoom && ready && isZoomed && (
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Reset}
          // Shift left of the export icon (top-right) when both are shown.
          className={classNames(styles.resetZoom, {
            [styles.resetZoomShifted]: !!exportConfig,
          })}
          onClick={resetZoom}
        >
          Reset Zoom
        </Button>
      )}
      {children}
      {isLoading && <div className={styles.loadingOverlay}>Loading…</div>}
    </div>
  );
};

SciChartView.displayName = 'SciChartView';
